import React, { useState, useEffect } from 'react';
import axios from 'axios';
import FrontDeskLogin from './FrontDeskLogin';
import LoadingSpinner from '@medical/shared/components/LoadingSpinner';
import ForcePasswordChange from '@medical/shared/components/ForcePasswordChange';
import { verifyStoredSession } from '@medical/shared/utils/verifyStoredSession';

const FrontDeskProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    checkFrontDeskAuthStatus();

    const handleStorageChange = (e) => {
      if (e.key === 'frontDeskToken' || e.key === null) {
        checkFrontDeskAuthStatus();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    const handleLogout = () => {
      setIsAuthenticated(false);
      setUser(null);
    };
    window.addEventListener('frontDeskLogout', handleLogout);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('frontDeskLogout', handleLogout);
    };
  }, []);

  const checkFrontDeskAuthStatus = async () => {
    try {
      const verified = await verifyStoredSession({
        tokenKey: 'frontDeskToken',
        refreshKey: 'frontDeskRefreshToken',
        userKey: 'frontDeskUser',
        role: 'front_desk',
        extraKeys: ['frontDeskSessionId']
      });
      setUser(verified);
      setIsAuthenticated(Boolean(verified));
      if (!verified && window.location.pathname !== '/front-desk') {
        window.history.replaceState({}, '', '/front-desk');
      }
    } catch (error) {
      console.error('Front desk auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLoginSuccess = (userData, token) => {
    setUser(userData);
    setIsAuthenticated(true);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <FrontDeskLogin onLoginSuccess={handleLoginSuccess} />;
  }

  if (user?.mustChangePassword) {
    return (
      <ForcePasswordChange
        accountLabel={user.frontDeskId || user.email}
        onSignOut={() => {
          ['frontDeskToken', 'frontDeskRefreshToken', 'frontDeskUser', 'frontDeskSessionId'].forEach((key) => localStorage.removeItem(key));
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          setIsAuthenticated(false);
        }}
        onChanged={() => {
          const next = { ...user, mustChangePassword: false };
          setUser(next);
          localStorage.setItem('frontDeskUser', JSON.stringify(next));
        }}
      />
    );
  }

  return children;
};

export default FrontDeskProtectedRoute;
